/**
 * Formatting helpers. `Intl` is the platform's — it downloads nothing, which is the
 * constraint this whole app is built under.
 *
 * ============================================================================
 * ⚠ THE MONEY RULE OF THIS APP, AND WHY THE FORMATTER LOOKS LIKE THIS
 * ============================================================================
 *
 * **No figure this portal shows is computed here. Every amount rendered is a field the
 * server sent, printed verbatim.** The wave that added the money screens is the wave
 * that says so, because this project has had ONE number — «what must I pay to be
 * switched back on» — wrong three times, on three surfaces, each of which re-derived it
 * (D64/D74 «requirement minus availability», D67 the fee instead of the pack, and the
 * per-Account share beside a contract-wide gate). `internal/personalarea/money.go`'s
 * header states it for the server; this file is the client half.
 *
 * So the conversion from the wire's MINOR UNITS to the two-decimal figure on screen is
 * **arithmetic-free**, and that is not a stunt:
 *
 *   - it makes the guard absolute. `src/money-arithmetic.test.ts` forbids an arithmetic
 *     operator beside a money-bearing identifier ANYWHERE in `src/`, with NO exemption
 *     list — not even for this file. An exemption is a hole somebody widens later;
 *   - it is exactly representable. 250 → «2,50» and 100000 → «1 000,00» are string
 *     operations on the integer's own decimal digits, so no division, no float, no
 *     `toFixed` rounding mode, and no value at which the display and the invoice can
 *     disagree by a kopeck.
 *
 * The separators are ru-RU's own: U+00A0 between thousands, a comma before the
 * kopecks. They are written out rather than taken from `Intl.NumberFormat`, because
 * `Intl` would need a NUMBER to format and producing one is the division this file
 * refuses to do.
 *
 * ⚠ NOTHING HERE HOLDS A RUSSIAN WORD. The currency symbol, the month names and every
 * status label live in `src/i18n/ru.ts` and are resolved by `src/money/labels.ts` — the
 * rule `src/strings.test.ts` enforces for the whole app, and it applies to a helper module
 * exactly as it applies to a component. So this file formats DIGITS and nothing else.
 */

const dateTime = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateOnly = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** The em dash every «no value» renders as. One character, one meaning, one place. */
export const NO_VALUE = '—';

/**
 * ru-RU's group separator: a NON-BREAKING space (U+00A0), so a figure never wraps across
 * a line on a 360-px screen. It is written as an ESCAPE and not as the character itself,
 * because an invisible literal is the one an editor's «trim whitespace» silently turns
 * into an ordinary space — after which the figure wraps and every test still passes.
 */
export const GROUP_SEPARATOR = '\u00A0';

/** ru-RU's decimal separator. */
export const DECIMAL_SEPARATOR = ',';

/**
 * formatDateTime renders an RFC 3339 instant in the BROWSER's own zone.
 *
 * The subscriber's zone and not the billing zone, deliberately: this value is «your
 * session is valid until», a fact about the phone in his hand. Every figure that means
 * something to BILLING — a charge date, a cycle boundary — is in the system billing
 * zone and must be formatted by whatever renders it, with that stated. Do not reuse
 * this function for one of those without reading that sentence again.
 *
 * An unparseable value renders as an em dash rather than «Invalid Date», which is what
 * `new Date('')` produces and what a subscriber would read as a broken portal.
 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_VALUE;
  return dateTime.format(d);
}

/**
 * formatInstantDay renders an RFC 3339 instant as a DAY, in the browser's zone.
 *
 * ⚠ THE ZONE IS A KNOWN APPROXIMATION AND IT IS RECORDED RATHER THAN GLOSSED. A
 * coverage-until or next-charge timestamp is a fact about the BILLING time zone
 * (Appendix H: «all cycle timestamps use the system billing time zone»), and this
 * listener publishes no such setting — the portal has five auth routes plus the money
 * reads and no public configuration endpoint. The operator and his subscribers are in
 * one zone at this installation, so the two agree today; a subscriber reading the
 * cabinet from another zone can see a boundary a day out. Giving the portal the real
 * answer means putting the billing zone on the wire, which is a server change and
 * belongs to whoever needs it.
 */
export function formatInstantDay(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_VALUE;
  return dateOnly.format(d);
}

/** formatInstantDateTime is [formatDateTime] tolerating null — the wire's own shape. */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  return formatDateTime(iso);
}

/**
 * formatCivilDate renders a YYYY-MM-DD CIVIL date, with no timezone anywhere near it.
 *
 * The installment schedule's «Дата платежа» is a civil date in the database and crosses
 * the wire as one (`paInstallmentStepWire.DueDate`), precisely so that no zone can move
 * it. Parsing it into a `Date` would put a UTC midnight on it and shift the 1st of the
 * month to the 30th for a subscriber west of the meridian, which is why this function
 * slices the string instead.
 */
export function formatCivilDate(value: string | null | undefined): string {
  if (!value) return NO_VALUE;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return NO_VALUE;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/**
 * shiftPeriod moves a YYYY-MM month by `months`, forward or back — the ‹ › spinner.
 *
 * ⚠ THIS IS CALENDAR ARITHMETIC AND NOT MONEY ARITHMETIC, and the distinction is the
 * whole point of `money-arithmetic.test.ts`'s vocabulary: a month is a label the
 * subscriber navigates by, and getting it wrong shows him the wrong page, which he can
 * see. Getting an AMOUNT wrong shows him a figure he cannot check. `Date.UTC` is used
 * because it is month arithmetic on a fictitious instant that is never displayed — the
 * result is immediately re-serialised to YYYY-MM.
 */
export function shiftPeriod(period: string, months: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  d.setUTCMonth(d.getUTCMonth() + months);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** currentPeriod is the browser's own month as YYYY-MM — the spinner's start. */
export function currentPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * formatMinor renders an integer number of MINOR units as a two-decimal figure.
 *
 * ⚠ NO ARITHMETIC — see this file's header. The integer's own decimal digits are split
 * with `slice`, so the value on screen is the value on the wire, exactly, for every
 * safe integer. A non-integer (or a NaN produced by a malformed body) renders as an em
 * dash rather than as «NaN» or as a plausible-looking wrong number.
 */
export function formatMinor(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isSafeInteger(minor)) return NO_VALUE;
  const text = String(minor);
  const negative = text.startsWith('-');
  const digits = negative ? text.slice(1) : text;
  const padded = digits.padStart(3, '0');
  const whole = padded.slice(0, -2);
  const fraction = padded.slice(-2);
  const grouped = whole.replace(/\B(?=(?:\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  return `${negative ? '-' : ''}${grouped}${DECIMAL_SEPARATOR}${fraction}`;
}

/**
 * parseMinor is [formatMinor] BACKWARDS — «1 000,50» → 100050 — and it is the ONE place
 * in this app where a money value is produced from something other than a server field.
 *
 * ============================================================================
 * ⚠ WHY THIS DOES NOT BREAK THE RULE THE FILE HEADER STATES
 * ============================================================================
 *
 * The rule is that no figure this portal SHOWS is computed here: every amount rendered
 * is a field the server sent. This function renders nothing. It reads what the
 * SUBSCRIBER typed into the «Сумма» box and turns his own decimal notation into the
 * minor units the wire speaks, so that `POST /pa/payments/card` receives the figure he
 * chose rather than a figure this app derived from some other one.
 *
 * The distinction is not a lawyer's: the three defects the rule exists for (D64/D74,
 * D67, the per-Account share beside a contract-wide gate) were all a surface DERIVING
 * «what must I pay» from other numbers. Nothing here derives anything — the prefill
 * comes from `prefill_amount`, which the server selects, and this function only changes
 * its notation back if the subscriber edits it.
 *
 * ⚠ AND IT IS ARITHMETIC-FREE, for the same reason `formatMinor` is: the digits of the
 * whole part and the two digits of the fraction are CONCATENATED, never multiplied by
 * 100. So `src/money-arithmetic.test.ts` needs no exemption for it either, and the
 * round trip `parseMinor(formatMinor(n)) === n` is exact for every safe integer rather
 * than «exact enough» — `format.test.ts` pins it.
 *
 * It REFUSES rather than repairs, and each refusal is deliberate:
 *
 *   - more than two decimals → `null`. Silently truncating «100,999» would charge a
 *     figure the subscriber did not type, which is the small version of the defect this
 *     whole rule is about;
 *   - a minus sign, a letter, two separators → `null`. There is no negative top-up;
 *   - an absurd number of digits → `null`, before `Number` can lose precision.
 *
 * `null` means «this is not a number» and the screen says so; it never means zero.
 */
export function parseMinor(text: string): number | null {
  // Spaces (including the non-breaking one `formatMinor` groups with) are noise a
  // subscriber pastes; the comma and the dot are both accepted because a phone keyboard
  // offers one and the catalogue prints the other.
  // The non-breaking space is written as an ESCAPE for the reason GROUP_SEPARATOR is:
  // an invisible literal is what an editor's «trim whitespace» silently converts, after
  // which a pasted «1 000,00» stops parsing and every test still passes.
  const compact = String(text).replace(/[\s\u00A0]/g, '').replace(/,/g, '.');
  const shape = /^(\d*)(?:\.(\d{0,2}))?$/.exec(compact);
  if (!shape) return null;
  const whole = shape[1] ?? '';
  const typedFraction = shape[2] ?? '';
  if (whole === '' && typedFraction === '') return null;
  const fraction = typedFraction.padEnd(2, '0');
  // CONCATENATION, not a multiplication — see the header.
  const digits = `${whole}${fraction}`;
  // 16 is the digit count of `Number.MAX_SAFE_INTEGER`; beyond it `Number` would round
  // silently, and `Number.isSafeInteger` below catches the 16-digit values that overflow.
  // The BUSINESS ceiling is the server's (a per-request sanity limit of 1 000 000,00) and
  // is deliberately not duplicated here — a second copy of a bound is a bound that drifts.
  if (digits.length > 16) return null;
  const value = Number(digits);
  return Number.isSafeInteger(value) ? value : null;
}

/** The byte thresholds, named so the branches below read as what they are. */
const KIB = 1024;
const MIB = 1048576;
const GIB = 1073741824;
const TIB = 1099511627776;

/**
 * formatBytesUnit reports a file size as a WHOLE number plus which unit it is in — the
 * ticket-attachment ceiling (`attachment_max_bytes`) and each attached file's own size.
 *
 * ⚠ IT DIVIDES, AND THAT IS LEGITIMATE HERE FOR THE REASON `shiftPeriod` DIVIDES NOTHING
 * AND `formatMinor` REFUSES TO. The rule this file's header states is about MONEY: getting
 * an amount wrong shows the subscriber a figure he cannot check, and every amount in this
 * app is a server field printed verbatim. A file size is not money — it is a magnitude the
 * subscriber compares against a file he is holding, and being out by a kilobyte changes
 * nothing he can act on. `money-arithmetic.test.ts`'s vocabulary is what encodes the
 * distinction: `bytes` is not one of its money words, exactly as `month` and `seq` are not.
 *
 * It returns the NUMBER and the unit KEY separately, because the unit is a Russian word and
 * `src/i18n/ru.ts` is the only place this app holds one (ST13) — a helper returning «10 МБ»
 * would be a helper holding a translation.
 */
export function formatBytesUnit(bytes: number | null | undefined): {
  value: number;
  unitKey: 'common.bytes' | 'common.kilobytes' | 'common.megabytes';
} {
  if (bytes === null || bytes === undefined || !Number.isSafeInteger(bytes) || bytes < 0) {
    return { value: 0, unitKey: 'common.bytes' };
  }
  if (bytes >= MIB) return { value: Math.round(bytes / MIB), unitKey: 'common.megabytes' };
  if (bytes >= KIB) return { value: Math.round(bytes / KIB), unitKey: 'common.kilobytes' };
  return { value: bytes, unitKey: 'common.bytes' };
}

/** The unit keys `formatOctets` can return. Russian words live in `src/i18n/ru.ts`. */
export type VolumeUnitKey =
  | 'common.bytes'
  | 'common.kilobytes'
  | 'common.megabytes'
  | 'common.gigabytes'
  | 'common.terabytes';

/**
 * formatOctets reports a TRAFFIC volume as a ru-RU number plus which unit it is in.
 *
 * ⚠ THE WIRE CARRIES OCTETS AND THE PORTAL OWNS THE UNIT, deliberately: `PAStatsDay`'s own
 * schema says so, because a server that pre-divided would have to choose one unit for every
 * scale from a kilobyte to a terabyte, and a subscriber's month spans three of them.
 *
 * ⚠ IT DIVIDES, AND THAT IS LEGITIMATE HERE FOR THE REASON `formatBytesUnit` ARGUES ABOVE.
 * The rule this file's header states is about MONEY: an amount got wrong shows the
 * subscriber a figure he cannot check, and every amount in this app is a server field
 * printed verbatim. A traffic volume is a magnitude he compares against his own sense of
 * what he downloaded, and being out by a megabyte in 8,5 ГБ changes nothing he can act on.
 * `money-arithmetic.test.ts`'s vocabulary encodes the distinction — `octets` is not one of
 * its money words, exactly as `bytes`, `month` and `seq` are not.
 *
 * ONE decimal place, from `Intl` through `toLocaleString` rather than from `toFixed`: the
 * rounding mode of `toFixed` is one nobody chose, and the guard forbids it outright. It
 * returns NULL — never a zero and never «—Б» — for a value that is not a usable count, so
 * the caller decides what «we have no number» looks like.
 */
export function formatOctets(
  octets: number | null | undefined,
): { value: string; unitKey: VolumeUnitKey } | null {
  if (octets === null || octets === undefined || !Number.isSafeInteger(octets) || octets < 0) {
    return null;
  }
  const one = (n: number): string => n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  if (octets >= TIB) return { value: one(octets / TIB), unitKey: 'common.terabytes' };
  if (octets >= GIB) return { value: one(octets / GIB), unitKey: 'common.gigabytes' };
  if (octets >= MIB) return { value: one(octets / MIB), unitKey: 'common.megabytes' };
  if (octets >= KIB) return { value: one(octets / KIB), unitKey: 'common.kilobytes' };
  return { value: one(octets), unitKey: 'common.bytes' };
}

/**
 * formatDuration splits a count of SECONDS into whole hours and the remaining minutes.
 *
 * It returns numbers and no words, for the reason the whole file does: `src/i18n/ru.ts` is
 * the only place this app keeps a Russian label, and «2 ч 12 мин» is two of them. The
 * caller picks the sentence — a session total under an hour reads better without «0 ч».
 *
 * A negative or unusable value is reported as null rather than as zero: `session_seconds`
 * is a sum the store computed, and printing «0 мин» for a body we could not read is the
 * silent-zero this whole surface is built to avoid.
 */
export function formatDuration(
  seconds: number | null | undefined,
): { hours: number; minutes: number } | null {
  if (seconds === null || seconds === undefined || !Number.isSafeInteger(seconds) || seconds < 0) {
    return null;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return { hours, minutes };
}

/**
 * signOf reports whether an amount is positive, negative or zero — for the COLOUR
 * §2.3 asks for («green when positive, red when negative»).
 *
 * A comparison and not a subtraction: it decides a class name, never a figure.
 */
export function signOf(minor: number | null | undefined): 'positive' | 'negative' | 'zero' {
  if (minor === null || minor === undefined || !Number.isSafeInteger(minor)) return 'zero';
  if (minor > 0) return 'positive';
  if (minor < 0) return 'negative';
  return 'zero';
}
